// Book-a-fee one-click (issue #108): POST /api/finance/processor-fees.
// The Record action on the always-actionable pin is PERIOD-SCOPED — it carries only the
// processor identity. The server resolves the OWED period (the A-selector) itself and STAMPS
// the fee's date into that period, so the pin is recordable before a report exists and a
// mis-dated fee can never leave the arm stuck. The processor's owed fee difference (gross
// estimate − already-recorded non-voided fees) is booked as an ordinary Confirmed Expenditure;
// the returned row rides the ledger, delta export, and per-payee itemization like any manual
// entry. The saved Expenditure in the write set refreshes the sort-inbox ledger, the report
// preflight, the report and finance health.

import { useMutation } from '@tanstack/react-query';
import type {
  BookOwedPeriodProcessorFeeRequest,
  BookProcessorFeeResponse,
  ExpenditureDto,
} from '@signalvote/shared';
import { apiJson } from './apiFetch';

/** Typed error carrying the server's message for a failed booking. */
export class BookProcessorFeeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BookProcessorFeeError';
  }
}

/**
 * Book the one-click processor fee for the owed period. Returns the created expenditure so the
 * caller can surface a confirmation; the write set refreshes what shows the fee.
 */
export function useBookProcessorFee() {
  return useMutation<ExpenditureDto, Error, BookOwedPeriodProcessorFeeRequest>({
    mutationFn: async ({ processorId }) => {
      const body = await apiJson<BookProcessorFeeResponse>('/api/finance/processor-fees', {
        method: 'POST',
        json: { processorId },
        error: (error) => new BookProcessorFeeError(error.error ?? 'Failed to book the processor fee'),
      });
      return body.expenditure;
    },
  });
}
